import React from 'react';
import I18n from 'i18n-js';
import { camelizeKeys, decamelizeKeys } from 'humps';
import { ChildProps } from 'react-apollo';
import gql from 'graphql-tag';
import { flowRight } from 'lodash';

import { IAddressFormData } from './addresses/address_form_data';
import { CountrySelector } from './discovery/country_selector';
import { ICountry } from '../schemas/core-schema';
import { CoreAddressFormFieldsConfigsQuery } from '../schemas/rql';
import AddressFormField from './address_form_field';
import { buildInputName, configFields } from '../lib/address_form_fields_helpers';
import {
  AddressInputField,
  LabelOverride,
  Field,
} from '../lib/address_form_fields_types';

import FormGroup from '@reverbdotcom/commons/src/components/form_group';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

interface Props {
  address: IAddressFormData;
  countries: ICountry[];
  updateField: (attributes) => void;
  fieldPrefix?: string;
  /**
   * fields that are optional in the config but should be required
   */
  requiredFieldsOverrides?: AddressInputField[];
  /**
   * fields that are in the config but should not render
   */
  hiddenFieldsOverrides?: AddressInputField[];
  /**
   * fields that should be visible but not editable
   */
  disabledFieldsOverrides?: AddressInputField[];
  labelOverrides?: LabelOverride;
  phoneLabelTooltip?: JSX.Element | React.ReactNode;
  /**
   * Pivots between use of camel and snake case since this
   * form is used in both frontend and Core.
   *
   * Address data submitted to RQL needs to be in camel case,
   * while the data being sent to Core should be in snake case.
   */
  useCamelizedFieldsForRQL?: boolean;
  /**
   * should the entire form be disabled (e.g. during submit operations)
   */
  disabled?: boolean;

  /**
   * should a set of frequently-used countries be elevated above other options in the country dropdown
   */
  prioritizeTopCountries?: boolean;
}

/**
 * This component should be used exclusively in frontend in components making RQL queries
 * modifying address data. When dealing with address forms talking directly
 * to our database in core, use the <AddressFormFieldsContainer /> with relevant field
 * prefixes instead.
 */
export function AddressFormFields(props: ChildProps<Props, CoreAddressFormFieldsConfigsQuery.Query>) {
  const { updateField, address, countries, useCamelizedFieldsForRQL, data, disabled, prioritizeTopCountries = false } = props;
  const { loading, intlAddressFormConfig } = data;
  const user = useUser();

  if (loading || !intlAddressFormConfig) return null;

  const fieldPrefix = props.fieldPrefix || '';
  const requiredFieldsOverrides = props.requiredFieldsOverrides || [];
  const hiddenFieldsOverrides = props.hiddenFieldsOverrides || [];
  const disabledFieldsOverrides = props.disabledFieldsOverrides || [];
  const labelOverrides = props.labelOverrides || {};

  const configs = intlAddressFormConfig?.configs;
  const addressFields = configFields(address.countryCode, configs);

  return (
    <div data-address-fields>
      <FormGroup label={I18n.t('discovery.addressFormFields.country')}>
        <CountrySelector
          inputName={buildInputName(fieldPrefix, 'country_code')}
          value={address.countryCode}
          onChange={e => handleCountryChange(e, addressFields, address, useCamelizedFieldsForRQL, updateField, configs)}
          countries={decamelizeKeys(countries)}
          disabled={disabled || disabledFieldsOverrides.includes('country_code')}
          prioritizeTopCountries={prioritizeTopCountries}
        />
      </FormGroup>

      {filterHidden(addressFields, hiddenFieldsOverrides).map((field) => (
        <AddressFormField
          key={field.fieldName}
          field={field}
          user={user}
          address={address}
          countries={countries}
          useCamelizedFieldsForRQL={useCamelizedFieldsForRQL}
          updateField={updateField}
          requiredFieldsOverrides={requiredFieldsOverrides}
          labelOverrides={labelOverrides}
          fieldPrefix={fieldPrefix}
          phoneLabelTooltip={props.phoneLabelTooltip}
          disabledFieldsOverrides={disabledFieldsOverrides}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function filterHidden(fields: Field[], hiddenFieldsOverrides: AddressInputField[]): Field[] {
  return fields.filter(field => !hiddenFieldsOverrides.includes(field.fieldName));
}

function handleCountryChange(evt, currentFields, address, useCamelizedFieldsForRQL, updateField, configs) {
  const newAddressState = buildDiffedAddressFieldsForCountryCode(address, currentFields, configs, evt);

  if (useCamelizedFieldsForRQL) {
    updateField(newAddressState);
  } else {
    updateField(decamelizeKeys(newAddressState));
  }
}

function buildDiffedAddressFieldsForCountryCode(
  address,
  currentFields: Field[],
  configs,
  evt,
): IAddressFormData {
  const fieldsFromConfig = configFields(evt, configs);
  const newFields = fieldsFromConfig.map((field) => field.fieldName);
  const oldFields = currentFields.map((field) => field.fieldName);
  const fieldsDifference = oldFields.filter((oldField) => !newFields.includes(oldField));
  const blankFields = fieldsDifference.reduce((prev, fieldName) => ({ ...prev, [fieldName]: '' }), {});

  return { ...address, ...camelizeKeys(blankFields), countryCode: evt };
}

const ADDRESS_FORM_FIELDS_CONFIGS_QUERY = gql`
  query Core_Address_Form_Fields_Configs_Query {
    intlAddressFormConfig {
      configs {
        countryCode
        fields {
          fieldName
          displayNames {
            locale
            displayName
          }
          required
        }
      }
    }
  }
`;

const connect = withGraphql<Props, CoreAddressFormFieldsConfigsQuery.Query>(
  ADDRESS_FORM_FIELDS_CONFIGS_QUERY,
  {
    options: {
      ssr: true,
    },
  },
);

export default flowRight([connect])(AddressFormFields);
